import { ColorSwatch, Flex, Text, Title } from "@mantine/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { PDFPage } from "./PDFPage";
import useReportStore from "../../../store/useReportStore";
import { PhasingOutSkill } from "../../../types/api";

interface Skill {
  name: string;
  color: string;
  description: string;
}

interface DataPoint {
  year: string;
  [key: string]: number | string;
}

const transformSkills = (
  phasingOutSkills: PhasingOutSkill[]
): { skills: Skill[]; data: DataPoint[] } => {
  const colors = ["#05D8D2", "#3A8BD9", "#6F40E2"];
  const skills: Skill[] = [];
  const skillNames: string[] = [];
  const data: DataPoint[] = [];

  phasingOutSkills.forEach((skill, index) => {
    skills.push({
      name: skill.title,
      color: colors[index % colors.length],
      description: skill.description,
    });

    skillNames.push(skill.title);

    Object.keys(skill.phasing).forEach((year) => {
      if (!data.find((d) => d.year === year)) {
        data.push({ year });
      }
      const yearData = data.find((d) => d.year === year);
      if (yearData) {
        yearData[skill.title] = skill.phasing[year];
      }
    });
  });

  return { skills, data };
};

export const SkillsPhasingOutPDF = () => {
  const { report } = useReportStore();

  const { skills, data } = report?.skills_phasing_out
    ? transformSkills(report.skills_phasing_out.data)
    : { skills: [], data: [] };

  const maxYAxisValue = Math.max(
    ...data.flatMap((d) =>
      Object.values(d).filter((v) => typeof v === "number")
    )
  );

  return (
    <PDFPage padding>
      <Title size={48} mb={8}>
        {report?.skills_phasing_out?.mark_down
          ? report?.skills_phasing_out?.mark_down.replace("##", "")
          : ""}
      </Title>
      <Text mb={48} size="28px" lh="42px" c="dimmed">
        {report?.skills_phasing_out?.explanation
          ? report?.skills_phasing_out?.explanation
          : ""}
      </Text>
      <ResponsiveContainer height={631} width="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis
            domain={[0, 200]}
            ticks={[
              0,
              maxYAxisValue / 4,
              maxYAxisValue / 2,
              (maxYAxisValue * 3) / 4,
              maxYAxisValue + maxYAxisValue/10,
            ]}
          />
          <Tooltip />
          {skills.map((skill, index) => (
            <Bar
              key={index}
              dataKey={skill.name}
              fill={skill.color}
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey={skill.name} position="top" />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>

      {skills.map((skill, index) => (
        <Flex key={index} gap={16} mt={48}>
          <ColorSwatch color={skill.color} size={28} radius={2} mt={10} />
          <Text size="28px" lh="42px">
            <b>{skill.name}</b>: {skill.description}
          </Text>
        </Flex>
      ))}
    </PDFPage>
  );
};
